import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Moon, Feather } from "lucide-react";

export function NightReflection() {
  const t = useT();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [tomorrow, setTomorrow] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() && !gratitude.trim()) return;
    setBusy(true);
    try {
      await api.post("/night/reflection", {
        text: text.trim(),
        reflection: text.trim(),
        gratitude: gratitude.trim() || null,
        tomorrow: tomorrow.trim() || null,
      });
      toast.success(t("common.saved"));
      setText(""); setGratitude(""); setTomorrow("");
      qc.invalidateQueries({ queryKey: ["quiet-returns"] });
    } catch (e) { toast.error(errMsg(e, t("errors.generic"))); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("client.reflection")} title={t("client.reflection")} subtitle={t("client.reflectionSub")} />
      <div className="rounded-3xl bg-white shadow-card p-6 md:p-8 space-y-5" data-testid="reflection-form">
        <div>
          <label className="text-sm text-ink-700 font-medium">Today, honestly…</label>
          <Textarea data-testid="reflection-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} className="mt-2 rounded-xl bg-cream-50" placeholder="A few sentences is enough." />
        </div>
        <div>
          <label className="text-sm text-ink-700 font-medium">A small gratitude</label>
          <Textarea data-testid="reflection-gratitude" value={gratitude} onChange={(e) => setGratitude(e.target.value)} rows={2} className="mt-2 rounded-xl bg-cream-50" />
        </div>
        <div>
          <label className="text-sm text-ink-700 font-medium">One kind thing for tomorrow</label>
          <Textarea data-testid="reflection-tomorrow" value={tomorrow} onChange={(e) => setTomorrow(e.target.value)} rows={2} className="mt-2 rounded-xl bg-cream-50" />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="reflection-submit" className="rounded-full h-11 px-5 bg-sage-500 hover:bg-sage-600">
            <Moon className="h-4 w-4 mr-1.5" /> {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function QuietReturns() {
  const t = useT();
  const q = useQuery({
    queryKey: ["quiet-returns"],
    queryFn: async () => (await api.get("/quiet-returns")).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.items || q.data?.entries || [];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("client.quietReturn")} title={t("client.quietReturn")} subtitle={t("brand.tagline")} />
      {q.isLoading && <LoadingCard />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={Feather} title={t("common.empty")} description="Your quiet returns and night reflections will gather here." testId="quiet-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="quiet-list">
        {items.map((r, i) => (
          <div key={r.id || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="text-xs text-ink-500">{r.created_at && new Date(r.created_at).toLocaleString()}</div>
            <div className="text-sm text-ink-900 mt-2 whitespace-pre-wrap">{r.text || r.reflection || r.summary || ""}</div>
            {r.gratitude && <div className="text-xs text-sage-700 mt-2 italic">"{r.gratitude}"</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
